import { useState } from 'react'
import type { JSX } from 'react'
import { api, call } from '@/api/client'
import type { Schema } from '@/api/client'
import { useMutation, useQuery } from '@/api/query'
import { useBrand } from '@/brand/context'
import { relativeTime } from '@/lib/format'
import { Button } from '@/ui/Button'
import { TextField } from '@/ui/Field'
import { Modal } from '@/ui/Modal'
import { Empty, ErrorState, InlineError, Loading } from '@/ui/states'
import { useToast } from '@/ui/toast-context'

type Endpoint = Schema<'EndpointOut'>
type Delivery = Schema<'DeliveryOut'>
type EventDefinition = Schema<'PreferenceOut'>

/**
 * Outbound webhooks, on the Notifications panel.
 *
 * S-D3 designed eight settings panels and webhooks is not one of them, so this
 * is a **second card on an approved panel** rather than a ninth entry in the
 * approved navigation — the alternative being an API a tenant cannot reach,
 * which is worse than either. Everything here is S-D1 components: the card, the
 * table, the field, the modal.
 *
 * **The secret is shown on demand, more than once.** It is derived, never
 * stored (`app/notifications/webhooks.py`), so there is nothing to reveal once
 * and lose; a show-once affordance would only mean a tenant who mislaid it had
 * to rotate, which invalidates every delivery already queued. The guard that
 * matters is the permission on the route.
 *
 * **The delivery log is what makes a failure legible.** Every attempt is a row
 * with its status code, the receiver's own message and when the next retry is
 * due — which is what the session's exit criterion means by "visible in the
 * delivery log", and what turns "our webhooks stopped" into a screenshot.
 */

/**
 * Mirrors `CreateEndpointRequest.url` in `openapi.json` (`minLength: 8,
 * maxLength: 500`). The minimum is the shortest thing that can be an `https://`
 * address at all, and the browser enforces both before submit (WEB-14).
 */
const URL_MIN = 8
const URL_MAX = 500

const STATE_TONE: Record<string, string> = {
  delivered: 'ar-badge--success',
  pending: 'ar-badge--info',
  parked: 'ar-badge--danger',
}

/**
 * SHELL-50. What an endpoint's subscription says, in S-D3's own words.
 *
 * Empty is *"All N"* rather than *"None"*, and that is the API's contract, not a
 * display choice: `EndpointOut.events` documents an empty list as **every event,
 * including ones added later**. Rendering it as "none" would tell a tenant their
 * endpoint receives nothing while it receives everything.
 *
 * A single subscribed event is named — the mockup's *"Replies only"* — because
 * one is the case where the name fits and is the thing worth knowing.
 */
function describeEvents(
  subscribed: readonly string[],
  catalogue: readonly EventDefinition[],
): string {
  if (subscribed.length === 0) {
    // The catalogue arrives with the panel above and can be empty for a moment,
    // or if that read failed. "All events" is true either way; "All 0" is not.
    return catalogue.length > 0 ? `All ${String(catalogue.length)}` : 'All events'
  }
  const [only] = subscribed
  if (subscribed.length === 1 && only !== undefined) {
    return catalogue.find((event) => event.event === only)?.label ?? only
  }
  return `${String(subscribed.length)} events`
}

/**
 * SHELL-64. One badge combining result and time — `✓ 200 · 12 min ago`.
 *
 * It was two columns and a relative time: an *Enabled/Off* badge, and "… ago"
 * with no outcome attached. So the row said an endpoint was enabled and had been
 * delivered to twelve minutes ago, and said nothing at all about whether that
 * delivery had worked — which is the only question anybody opens this table to
 * answer. The status code comes from `last_status_code`, added for this row.
 *
 * **The tick is `last_success_at === last_delivery_at`, not
 * `consecutive_failures === 0`.** Those look equivalent and are not: re-enabling
 * a disabled endpoint resets the failure counter (`update_endpoint` clears it
 * along with `disabled_at` and `disabled_reason`) and touches neither timestamp
 * nor the status code — so an endpoint switched off after five 500s and then
 * re-enabled rendered `✓ 500 · 3 hours ago`, a success tick against a server
 * error. Comparing the two timestamps asks the question the badge is actually
 * about: *did the last settled attempt succeed*. A success writes both; a
 * failure writes only `last_delivery_at`.
 *
 * The status code is shown but never decides the tone, because a failure with no
 * response at all — DNS, TLS, a timeout — has no code and is still a failure.
 * The attempt count replaces the time in that case, as the mockup does: for an
 * endpoint that is down, *how many times* has overtaken *when*.
 */
function lastDelivery(endpoint: Endpoint, now: Date): JSX.Element {
  if (endpoint.last_delivery_at === null) {
    return <span className="ar-muted">Never</span>
  }
  const code = endpoint.last_status_code === null ? '' : ` ${String(endpoint.last_status_code)}`
  if (endpoint.last_success_at !== endpoint.last_delivery_at) {
    // `consecutive_failures` can be 0 here — see the note above on re-enabling —
    // so the count falls back to "1 attempt" rather than printing "0 attempts"
    // about a delivery that demonstrably failed.
    const attempts = Math.max(endpoint.consecutive_failures, 1)
    return (
      <span className="ar-badge ar-badge--danger">
        ✕{code} · {attempts} attempt{attempts === 1 ? '' : 's'}
      </span>
    )
  }
  return (
    <span className="ar-badge ar-badge--success">
      ✓{code} · {relativeTime(endpoint.last_delivery_at, now)}
    </span>
  )
}

export function WebhooksCard({ events }: { events: readonly EventDefinition[] }): JSX.Element {
  const brand = useBrand()
  const toast = useToast()
  const now = new Date()
  const [adding, setAdding] = useState(false)
  const [url, setUrl] = useState('')
  const [description, setDescription] = useState('')
  const [chosen, setChosen] = useState<readonly string[]>([])
  const [revealed, setRevealed] = useState<Record<string, string>>({})
  const [confirmDelete, setConfirmDelete] = useState<Endpoint | null>(null)
  const [managing, setManaging] = useState<Endpoint | null>(null)
  // SHELL-51. Which delivery's response body is open. One at a time: the log is
  // a table, and two expanded bodies would push every other row off the screen.
  const [openBody, setOpenBody] = useState<number | null>(null)

  const endpoints = useQuery((signal) => call(() => api.GET('/api/v1/webhooks', { signal })), [])
  const deliveries = useQuery(
    (signal) =>
      call(() =>
        api.GET('/api/v1/webhooks/deliveries', { params: { query: { limit: 20 } }, signal }),
      ),
    [],
  )

  const create = useMutation(
    (body: { url: string; description: string; events: readonly string[] }) =>
      call(() =>
        api.POST('/api/v1/webhooks', {
          body: {
            url: body.url,
            description: body.description || null,
            // SHELL-50. Empty means *every event, including ones added later*,
            // which is the API's own contract and the right default — an
            // endpoint enumerating today's catalogue would silently stop
            // receiving whatever ships next. It is a choice now rather than a
            // hardcoded `[]`: the dialog offers the list and sends nothing only
            // when nothing was ticked.
            events: [...body.events],
          },
        }),
      ),
  )
  // `null` rather than an omitted key for both fields: `exactOptionalPropertyTypes`
  // is on, and `UpdateEndpointRequest` reads `null` as "leave this alone" —
  // which is the same thing an omission means and is expressible in the type.
  const patch = useMutation(
    (body: { id: string; enabled?: boolean | null; events?: string[] | null }) =>
      call(() =>
        api.PATCH('/api/v1/webhooks/{endpoint_id}', {
          params: { path: { endpoint_id: body.id } },
          body: { enabled: body.enabled ?? null, events: body.events ?? null },
        }),
      ),
  )
  const remove = useMutation((id: string) =>
    call(() =>
      api.DELETE('/api/v1/webhooks/{endpoint_id}', { params: { path: { endpoint_id: id } } }),
    ),
  )
  const reveal = useMutation((id: string) =>
    call(() =>
      api.GET('/api/v1/webhooks/{endpoint_id}/secret', { params: { path: { endpoint_id: id } } }),
    ),
  )
  const rotate = useMutation((id: string) =>
    call(() =>
      api.POST('/api/v1/webhooks/{endpoint_id}/rotate-secret', {
        params: { path: { endpoint_id: id } },
      }),
    ),
  )
  const retry = useMutation((id: number) =>
    call(() =>
      api.POST('/api/v1/webhooks/deliveries/{delivery_id}/retry', {
        params: { path: { delivery_id: id } },
      }),
    ),
  )

  const rows = endpoints.state.data?.endpoints ?? []
  const log = deliveries.state.data?.deliveries ?? []

  function rotateSecret(target: Endpoint): void {
    void (async () => {
      const result = await rotate.run(target.id)
      if (!result.ok) return
      // Filtered rather than deleted: the revealed secret is no longer the
      // endpoint's after a rotation, and leaving it on screen would show a
      // value that no longer verifies anything.
      setRevealed((current) =>
        Object.fromEntries(Object.entries(current).filter(([id]) => id !== target.id)),
      )
      refreshAll()
      toast.show({
        kind: 'success',
        title: 'Signing secret rotated',
        body: 'Deliveries already queued keep the old one.',
      })
    })()
  }

  function retryDelivery(id: number): void {
    void (async () => {
      const result = await retry.run(id)
      if (!result.ok) return
      deliveries.refetch()
      toast.show({ kind: 'success', title: 'Queued for another attempt' })
    })()
  }

  /** The newest parked delivery for an endpoint — what the row's *Retry* retries. */
  function parkedFor(endpoint: Endpoint): Delivery | undefined {
    return log.find((row) => row.endpoint_id === endpoint.id && row.state === 'parked')
  }

  function refreshAll(): void {
    endpoints.refetch()
    deliveries.refetch()
  }

  return (
    <>
      <section className="ar-card">
        <div className="ar-card__header">
          <h2>Webhooks</h2>
          <span className="ar-card__spacer" />
          <Button
            tone="ghost"
            size="sm"
            onClick={() => {
              create.reset()
              setUrl('')
              setDescription('')
              setChosen([])
              setAdding(true)
            }}
          >
            {/* SHELL-66. S-D3's label. */}
            Add an endpoint
          </Button>
        </div>

        <InlineError
          failure={
            patch.state.failure ??
            remove.state.failure ??
            reveal.state.failure ??
            rotate.state.failure
          }
        />

        {/* SHELL-66. S-D3's body intro. It says the two things a reader has to
            know before they trust the feature — every request is signed, and a
            failing endpoint is retried on a bounded ladder and then disabled —
            and the card had neither, so the breaker that switches an endpoint
            off arrived as a surprise. */}
        <div className="ar-card__body" style={{ paddingBottom: 0 }}>
          <p className="ar-muted ar-text-body-sm" style={{ margin: 0 }}>
            The one notification channel that goes to a machine rather than a person. Every request
            is signed, so your endpoint can prove it came from us, and a failing endpoint is retried
            on a bounded ladder and then disabled — never retried forever.
          </p>
        </div>

        {endpoints.state.status === 'loading' ? (
          <div className="ar-card__body">
            <Loading label="Loading webhook endpoints" lines={2} />
          </div>
        ) : null}

        {endpoints.state.status === 'error' ? (
          <ErrorState
            failure={endpoints.state.failure}
            title="Could not load your webhook endpoints"
            onRetry={endpoints.refetch}
          />
        ) : null}

        {endpoints.state.status === 'ready' && rows.length === 0 ? (
          <div className="ar-card__body">
            <Empty
              icon="↗"
              title="No webhook endpoints"
              body={`Add one and ${brand.productName} will POST every notification to it, signed, with bounded retries.`}
            />
          </div>
        ) : null}

        {endpoints.state.status === 'ready' && rows.length > 0 ? (
          <div className="ar-table-wrap" tabIndex={0}>
            <table className="ar-table">
              <thead>
                <tr>
                  <th>Endpoint</th>
                  {/* SHELL-50. S-D3's third column, and the API has always
                      supported it — the card just never sent or showed it. */}
                  <th>Events</th>
                  <th>Last delivery</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id}>
                    <td>
                      <strong className="ar-mono">{row.url}</strong>
                      {row.description ? (
                        <div
                          className="ar-subtle"
                          style={{ fontSize: 'var(--ar-text-caption-size)' }}
                        >
                          {row.description}
                        </div>
                      ) : null}
                      {/*
                        The enabled state moved out of its own column and under
                        the address, because the mockup's three columns are
                        Endpoint · Events · Last delivery and *disabled* is a
                        thing an endpoint rarely is. It is still a badge, and a
                        danger one, so an endpoint the breaker switched off is
                        not quieter than one that is working.
                      */}
                      {row.enabled ? null : (
                        <div style={{ marginTop: 'var(--ar-space-2)' }}>
                          <span className="ar-badge ar-badge--danger">Off</span>{' '}
                          {row.disabled_reason ? (
                            <span
                              className="ar-subtle"
                              style={{ fontSize: 'var(--ar-text-caption-size)' }}
                            >
                              {row.disabled_reason}
                            </span>
                          ) : null}
                        </div>
                      )}
                    </td>
                    <td className="ar-muted">{describeEvents(row.events, events)}</td>
                    <td>{lastDelivery(row, now)}</td>
                    <td className="ar-num">
                      {/*
                        The mockup's inline action (parity P-NTF-09, 2026-09-13):
                        *Retry* on an endpoint whose last delivery failed and has
                        a parked attempt to re-queue, *Rotate secret* otherwise —
                        `shell.html` draws exactly those two. *Manage* stays
                        beside it (SHELL-65's dialog: show secret, disable,
                        remove — three things the design did not know about,
                        and none of them is dropped).
                      */}
                      {(() => {
                        const parked =
                          row.last_success_at !== row.last_delivery_at ? parkedFor(row) : undefined
                        return parked ? (
                          <Button
                            tone="quiet"
                            size="sm"
                            disabled={retry.pending}
                            onClick={() => {
                              retryDelivery(parked.id)
                            }}
                          >
                            Retry
                          </Button>
                        ) : (
                          <Button
                            tone="quiet"
                            size="sm"
                            disabled={rotate.pending}
                            onClick={() => {
                              rotateSecret(row)
                            }}
                          >
                            Rotate secret
                          </Button>
                        )
                      })()}{' '}
                      <Button
                        tone="quiet"
                        size="sm"
                        onClick={() => {
                          setManaging(row)
                        }}
                      >
                        Manage
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}

        {/* SHELL-66, then parity P-NTF-07 (2026-09-13): the footer is the
            mockup's sentence alone. The signature format — the one thing a
            developer implementing the receiver cannot guess — moved into the
            Manage dialog, beside the secret it is computed with. */}
        <div className="ar-card__footer">
          <span className="ar-subtle" style={{ fontSize: 'var(--ar-text-body-sm-size)' }}>
            The delivery log keeps the last 30 days, including the response body, so a failure can
            be diagnosed without asking us for it.
          </span>
        </div>
      </section>

      <section className="ar-card">
        <div className="ar-card__header">
          <h2>Delivery log</h2>
          <span className="ar-card__spacer" />
          <Button
            tone="quiet"
            size="sm"
            onClick={() => {
              deliveries.refetch()
            }}
          >
            Refresh
          </Button>
        </div>

        <InlineError failure={retry.state.failure} />

        {deliveries.state.status === 'loading' ? (
          <div className="ar-card__body">
            <Loading label="Loading the delivery log" lines={3} />
          </div>
        ) : null}

        {deliveries.state.status === 'error' ? (
          <ErrorState
            failure={deliveries.state.failure}
            title="Could not load the delivery log"
            onRetry={deliveries.refetch}
          />
        ) : null}

        {deliveries.state.status === 'ready' && log.length === 0 ? (
          <div className="ar-card__body">
            <Empty
              icon="○"
              title="Nothing delivered yet"
              body="Attempts appear here as they happen, with the status code and the next retry."
            />
          </div>
        ) : null}

        {deliveries.state.status === 'ready' && log.length > 0 ? (
          <div className="ar-table-wrap" tabIndex={0}>
            <table className="ar-table">
              <thead>
                <tr>
                  <th>Event</th>
                  <th>State</th>
                  <th>Attempts</th>
                  <th>Result</th>
                  <th>When</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {log.map((row: Delivery) => (
                  <tr key={row.id}>
                    <td>
                      <strong>{row.event}</strong>
                      <div
                        className="ar-subtle ar-mono"
                        style={{ fontSize: 'var(--ar-text-caption-size)' }}
                      >
                        {row.url}
                      </div>
                    </td>
                    <td>
                      <span className={`ar-badge ${STATE_TONE[row.state] ?? ''}`}>{row.state}</span>
                    </td>
                    <td className="ar-num">
                      {row.attempts} / {row.max_attempts}
                    </td>
                    <td>
                      {row.last_status_code !== null ? (
                        <span className="ar-mono">{row.last_status_code}</span>
                      ) : null}
                      {row.last_error ? (
                        <div
                          className="ar-subtle"
                          style={{ fontSize: 'var(--ar-text-caption-size)' }}
                        >
                          {row.last_error}
                        </div>
                      ) : null}
                      {/*
                        SHELL-51. The card's footer has always promised the log
                        keeps "the response body, so a failure can be diagnosed
                        without asking us for it" — and the body was on the wire
                        as `response_snippet` and rendered nowhere, so the
                        sentence was false. Behind a press rather than inline:
                        it is the receiver's own output, of unknown length and
                        shape, and it belongs in the row it explains rather than
                        widening every row that has one.
                      */}
                      {row.response_snippet ? (
                        <div style={{ marginTop: 'var(--ar-space-2)' }}>
                          <Button
                            tone="quiet"
                            size="sm"
                            onClick={() => {
                              setOpenBody((current) => (current === row.id ? null : row.id))
                            }}
                          >
                            {openBody === row.id ? 'Hide response' : 'Show response'}
                          </Button>
                          {openBody === row.id ? (
                            <pre
                              className="ar-mono"
                              style={{
                                marginTop: 'var(--ar-space-2)',
                                marginBottom: 0,
                                maxWidth: '32rem',
                                maxHeight: '12rem',
                                overflow: 'auto',
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                                fontSize: 'var(--ar-text-caption-size)',
                              }}
                            >
                              {row.response_snippet}
                            </pre>
                          ) : null}
                        </div>
                      ) : null}
                    </td>
                    <td className="ar-muted">
                      {row.state === 'pending'
                        ? `Next ${relativeTime(row.next_attempt_at, now)}`
                        : relativeTime(row.delivered_at ?? row.created_at, now)}
                    </td>
                    <td className="ar-num">
                      {row.state === 'parked' ? (
                        <Button
                          tone="quiet"
                          size="sm"
                          onClick={() => {
                            retryDelivery(row.id)
                          }}
                        >
                          Retry
                        </Button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </section>

      {adding ? (
        <Modal
          title="Add a webhook endpoint"
          onClose={() => {
            setAdding(false)
          }}
          footer={
            <>
              <Button
                tone="ghost"
                onClick={() => {
                  setAdding(false)
                }}
              >
                Cancel
              </Button>
              <Button
                tone="primary"
                writes
                busy={create.pending}
                busyLabel="Adding"
                onClick={() => {
                  void (async () => {
                    const result = await create.run({ url, description, events: chosen })
                    if (!result.ok) return
                    setAdding(false)
                    refreshAll()
                    toast.show({
                      kind: 'success',
                      title: 'Endpoint added',
                      body: 'Show its secret to verify the signature on every delivery.',
                    })
                  })()
                }}
              >
                Add endpoint
              </Button>
            </>
          }
        >
          <InlineError failure={create.state.failure} />
          <TextField
            label="URL"
            type="url"
            value={url}
            required
            minLength={URL_MIN}
            maxLength={URL_MAX}
            placeholder="https://example.com/augrank"
            help={`https only. ${brand.productName} fetches this from its own servers, so an address on a private network is refused.`}
            onChange={(event) => {
              setUrl(event.currentTarget.value)
            }}
          />
          <TextField
            label="Description"
            value={description}
            placeholder="Our CRM"
            help="Optional. Shown in this list so you can tell two endpoints apart."
            onChange={(event) => {
              setDescription(event.currentTarget.value)
            }}
          />
          <EventPicker catalogue={events} chosen={chosen} onChange={setChosen} />
        </Modal>
      ) : null}

      {managing ? (
        <ManageDialog
          endpoint={managing}
          catalogue={events}
          secret={revealed[managing.id] ?? null}
          busy={patch.pending || rotate.pending || reveal.pending}
          onClose={() => {
            setManaging(null)
            // Forgotten on close. A derived secret can be shown again in one
            // press, so keeping it in state buys nothing — and the one thing it
            // does buy is a signing key still on screen behind a dialog nobody
            // is looking at, which is how it reaches a support screenshot.
            setRevealed({})
          }}
          onReveal={() => {
            const target = managing
            void (async () => {
              const result = await reveal.run(target.id)
              if (result.ok) {
                setRevealed((current) => ({ ...current, [target.id]: result.data.secret }))
              }
            })()
          }}
          onRotate={() => {
            rotateSecret(managing)
          }}
          onToggle={() => {
            const target = managing
            void (async () => {
              const result = await patch.run({ id: target.id, enabled: !target.enabled })
              if (!result.ok) return
              setManaging(null)
              refreshAll()
            })()
          }}
          onSaveEvents={(next) => {
            const target = managing
            void (async () => {
              const result = await patch.run({ id: target.id, events: [...next] })
              if (!result.ok) return
              setManaging(null)
              refreshAll()
              toast.show({ kind: 'success', title: 'Subscription saved' })
            })()
          }}
          onRemove={() => {
            setConfirmDelete(managing)
            setManaging(null)
          }}
        />
      ) : null}

      {confirmDelete ? (
        <Modal
          title="Remove this endpoint?"
          onClose={() => {
            setConfirmDelete(null)
          }}
          footer={
            <>
              <Button
                tone="ghost"
                onClick={() => {
                  setConfirmDelete(null)
                }}
              >
                Keep it
              </Button>
              <Button
                tone="danger"
                writes
                busy={remove.pending}
                busyLabel="Removing"
                onClick={() => {
                  const target = confirmDelete
                  void (async () => {
                    const result = await remove.run(target.id)
                    if (!result.ok) return
                    setConfirmDelete(null)
                    refreshAll()
                    toast.show({ kind: 'success', title: 'Endpoint removed' })
                  })()
                }}
              >
                Remove
              </Button>
            </>
          }
        >
          <p style={{ marginTop: 0 }}>
            {brand.productName} will stop posting to{' '}
            <span className="ar-mono">{confirmDelete.url}</span>. Its delivery history stays, so you
            can still see what was sent.
          </p>
        </Modal>
      ) : null}
    </>
  )
}

/**
 * SHELL-50. Which events an endpoint receives.
 *
 * Nothing ticked means **every event**, and the caption says so rather than
 * leaving an empty list to be read as "none" — that is `EndpointOut.events`'s
 * documented contract and the only default that keeps working when the
 * catalogue grows.
 *
 * A checkbox list rather than a multi-select: the catalogue is short, each entry
 * has a label written for a person, and a `<select multiple>` is the control
 * everyone mis-operates.
 */
function EventPicker({
  catalogue,
  chosen,
  onChange,
}: {
  catalogue: readonly EventDefinition[]
  chosen: readonly string[]
  onChange: (next: readonly string[]) => void
}): JSX.Element | null {
  // Nothing to choose from — the catalogue read failed, or has not landed. The
  // endpoint still works and still receives everything; offering an empty
  // fieldset would only imply otherwise.
  if (catalogue.length === 0) return null

  return (
    <fieldset style={{ border: 0, margin: 0, padding: 0 }}>
      <legend className="ar-field__label">Events</legend>
      <div className="ar-stack ar-stack--tight">
        {catalogue.map((event) => (
          <label className="ar-choice ar-choice--check ar-text-body-sm" key={event.event}>
            <input
              type="checkbox"
              checked={chosen.includes(event.event)}
              onChange={(input) => {
                onChange(
                  input.currentTarget.checked
                    ? [...chosen, event.event]
                    : chosen.filter((name) => name !== event.event),
                )
              }}
            />
            <span className="ar-choice__box" aria-hidden="true" />
            <span>{event.label}</span>
          </label>
        ))}
      </div>
      <span className="ar-field__help">
        {chosen.length === 0
          ? 'Nothing ticked means every event, including ones we add later.'
          : `${String(chosen.length)} of ${String(catalogue.length)} events.`}
      </span>
    </fieldset>
  )
}

/**
 * SHELL-65. Everything the endpoint row used to carry as four buttons.
 *
 * The row now has one action and this is what it opens. Grouping them also
 * makes two things legible that four buttons in a cell could not: the secret is
 * shown *beside* the rotate that would invalidate it, and Remove sits at the
 * bottom under its own heading rather than one pixel from Disable.
 *
 * The subscription is edited here too, because it is the same kind of thing —
 * how this endpoint is configured — and the alternative was a second dialog.
 */
function ManageDialog({
  endpoint,
  catalogue,
  secret,
  busy,
  onClose,
  onReveal,
  onRotate,
  onToggle,
  onSaveEvents,
  onRemove,
}: {
  endpoint: Endpoint
  catalogue: readonly EventDefinition[]
  secret: string | null
  busy: boolean
  onClose: () => void
  onReveal: () => void
  onRotate: () => void
  onToggle: () => void
  onSaveEvents: (next: readonly string[]) => void
  onRemove: () => void
}): JSX.Element {
  const brand = useBrand()
  const [chosen, setChosen] = useState<readonly string[]>(endpoint.events)
  const dirty =
    chosen.length !== endpoint.events.length ||
    chosen.some((name) => !endpoint.events.includes(name))

  return (
    <Modal
      title="Manage endpoint"
      onClose={onClose}
      footer={
        <>
          <Button tone="ghost" onClick={onClose}>
            Close
          </Button>
          <Button
            tone="primary"
            busy={busy}
            disabled={!dirty}
            onClick={() => {
              onSaveEvents(chosen)
            }}
          >
            Save events
          </Button>
        </>
      }
    >
      <p className="ar-mono" style={{ marginTop: 0, wordBreak: 'break-all' }}>
        {endpoint.url}
      </p>

      <EventPicker catalogue={catalogue} chosen={chosen} onChange={setChosen} />

      <p style={{ marginBottom: 'var(--ar-space-2)' }}>
        <strong>Signing secret</strong>
      </p>
      {/* Moved here from the card's footer (parity P-NTF-07): the format is
          the one thing a receiver cannot guess, and the secret it uses is the
          one this dialog shows. */}
      <p className="ar-muted" style={{ marginTop: 0, fontSize: 'var(--ar-text-body-sm-size)' }}>
        Every delivery carries <span className="ar-mono">X-AugRank-Signature</span>:{' '}
        <span className="ar-mono">t=&lt;unix&gt;,v1=HMAC-SHA256(secret, &quot;t.body&quot;)</span>.
        Verify it before you trust the body.
      </p>
      {secret ? (
        <p className="ar-mono" style={{ marginTop: 0, wordBreak: 'break-all' }}>
          {secret}
        </p>
      ) : null}
      <div className="ar-row" style={{ gap: 'var(--ar-space-3)' }}>
        <Button tone="quiet" size="sm" disabled={busy} onClick={onReveal}>
          {secret ? 'Show again' : 'Show secret'}
        </Button>
        <Button tone="quiet" size="sm" disabled={busy} onClick={onRotate}>
          Rotate secret
        </Button>
        <Button tone="quiet" size="sm" disabled={busy} onClick={onToggle}>
          {endpoint.enabled ? 'Disable' : 'Enable'}
        </Button>
      </div>

      <p style={{ marginBottom: 'var(--ar-space-2)' }}>
        <strong>Remove</strong>
      </p>
      <p className="ar-muted" style={{ marginTop: 0 }}>
        {brand.productName} stops posting to this address. Its delivery history stays.
      </p>
      <Button tone="danger" size="sm" onClick={onRemove}>
        Remove endpoint
      </Button>
    </Modal>
  )
}
